import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Coins, 
  TrendingDown, 
  AlertTriangle, 
  CreditCard, 
  X, 
  CheckCircle2, 
  XCircle, 
  Info, 
  AlertCircle 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useNotification, Notification, NotificationType } from "@/contexts/NotificationContext";

const NotificationItem = ({ 
  notification, 
  onClose 
}: { 
  notification: Notification; 
  onClose: () => void;
}) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (notification.duration) {
      timerRef.current = setTimeout(() => {
        onClose();
      }, notification.duration);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [notification.duration, onClose]);

  const handleDismiss = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    onClose();
  };

  // Credit Deduction Notification
  if (notification.type === "credit-deduction") {
    const amount = notification.data?.amount || 0;
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.5, y: -50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.5, y: 50 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="pointer-events-auto"
      >
        <div className="bg-gradient-to-r from-destructive to-destructive/80 text-destructive-foreground rounded-xl shadow-2xl p-4 min-w-[200px] border-2 border-destructive/30">
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ rotate: [0, -15, 15, -10, 10, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 0.6, repeat: 2, repeatDelay: 0.5 }}
            >
              <Coins className="h-8 w-8" />
            </motion.div>
            
            <div className="flex-1">
              <motion.div
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="flex items-center gap-2"
              >
                <TrendingDown className="h-5 w-5" />
                <span className="text-2xl font-bold">{amount}</span>
              </motion.div>
              <motion.p 
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-xs opacity-90 mt-0.5"
              >
                Kredi düşüldü
              </motion.p>
            </div>
          </div>

          <motion.div className="mt-3 h-1 bg-destructive-foreground/20 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-destructive-foreground/60"
              initial={{ width: "100%" }}
              animate={{ width: "0%" }}
              transition={{ duration: (notification.duration || 5000) / 1000, ease: "linear" }}
            />
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Insufficient Credit Notification
  if (notification.type === "insufficient-credit") {
    const { currentCredits, requiredCredits } = notification.data || {};
    
    const handleBuyCredits = () => {
      handleDismiss();
      navigate("/pricing");
    };

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9, x: 100 }}
        animate={{ opacity: 1, scale: 1, x: 0 }}
        exit={{ opacity: 0, scale: 0.9, x: 100 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="pointer-events-auto max-w-sm"
      >
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-xl shadow-2xl overflow-hidden border-2 border-amber-400/30">
          <div className="p-4 pb-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 0.5, repeat: 3, repeatDelay: 1 }}
                  className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0"
                >
                  <AlertTriangle className="h-5 w-5" />
                </motion.div>
                
                <div>
                  <motion.h3
                    initial={{ y: -10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="font-bold text-lg"
                  >
                    Yetersiz Kredi
                  </motion.h3>
                  <motion.p
                    initial={{ y: -10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="text-sm text-white/80"
                  >
                    {currentCredits} / {requiredCredits} kredi
                  </motion.p>
                </div>
              </div>
              
              <button 
                onClick={handleDismiss}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="px-4 pb-3"
          >
            <p className="text-sm text-white/90">
              Video oluşturmak için en az {requiredCredits} kredi gerekli.
            </p>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="px-4 pb-4 flex gap-2"
          >
            <Button
              onClick={handleBuyCredits}
              size="sm"
              className="flex-1 bg-white text-amber-600 hover:bg-white/90 font-semibold gap-2"
            >
              <CreditCard className="h-4 w-4" />
              Kredi Al
            </Button>
            <Button
              onClick={handleDismiss}
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/20 hover:text-white"
            >
              İptal
            </Button>
          </motion.div>

          <motion.div className="h-1 bg-white/20">
            <motion.div
              className="h-full bg-white/60"
              initial={{ width: "100%" }}
              animate={{ width: "0%" }}
              transition={{ duration: (notification.duration || 8000) / 1000, ease: "linear" }}
            />
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Generic Notifications (success, error, info, warning)
  const typeConfig: Record<NotificationType, { 
    icon: React.ElementType; 
    bgClass: string; 
    iconClass: string;
  }> = {
    success: { 
      icon: CheckCircle2, 
      bgClass: "bg-gradient-to-r from-emerald-500 to-green-600 text-white border-emerald-400/30",
      iconClass: "bg-white/20"
    },
    error: { 
      icon: XCircle, 
      bgClass: "bg-gradient-to-r from-red-500 to-rose-600 text-white border-red-400/30",
      iconClass: "bg-white/20"
    },
    info: { 
      icon: Info, 
      bgClass: "bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-blue-400/30",
      iconClass: "bg-white/20"
    },
    warning: { 
      icon: AlertCircle, 
      bgClass: "bg-gradient-to-r from-yellow-500 to-amber-600 text-white border-yellow-400/30",
      iconClass: "bg-white/20"
    },
    "credit-deduction": { icon: Coins, bgClass: "", iconClass: "" },
    "insufficient-credit": { icon: AlertTriangle, bgClass: "", iconClass: "" },
  };

  const config = typeConfig[notification.type];
  const Icon = config.icon;
  const { title, message } = notification.data || {};

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, x: 100 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.9, x: 100 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="pointer-events-auto max-w-sm"
    >
      <div className={`rounded-xl shadow-2xl overflow-hidden border-2 ${config.bgClass}`}>
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${config.iconClass}`}>
                <Icon className="h-5 w-5" />
              </div>
              
              <div>
                <h3 className="font-bold text-base">{title}</h3>
                {message && (
                  <p className="text-sm opacity-90 mt-0.5">{message}</p>
                )}
              </div>
            </div>
            
            <button 
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <motion.div className="h-1 bg-white/20">
          <motion.div
            className="h-full bg-white/60"
            initial={{ width: "100%" }}
            animate={{ width: "0%" }}
            transition={{ duration: (notification.duration || 5000) / 1000, ease: "linear" }}
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

export const NotificationCenter = () => {
  const { notifications, removeNotification } = useNotification();

  return (
    <div className="fixed top-20 right-4 z-[100] flex flex-col gap-3 pointer-events-none">
      <AnimatePresence mode="popLayout">
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};
